<script setup lang="ts">
import type {
  OCGCurveType,
  OCGTerritory,
} from '@src/store/analysis/data-typing';

import { computed, onMounted, ref, watch } from 'vue';

import { useViewportActions } from '@/src/components/pixel-overlay/useViewportActions';
import { useAnalysisStore } from '@/src/store/analysis';
import { useViewportStateStore } from '@/src/store/contours/viewport-state-store';

import AnalysisTableCarousel from './analysis/AnalysisTableCarousel.vue';
import BiomarkerValuesTable from './analysis/BiomarkerValuesTable.vue';
// import RadarPlot from './analysis/RadarPlot.vue';
// import ClassificationRadarPlot from './analysis/ClassificationRadarPlot.vue';
import PolarmapSectorsManager from './analysis/PolarmapSectorsManager.vue';
import AnalysisGraph from './AnalysisGraph.vue';
import GlassDropdown from './GlassDropdown.vue';
// import TempCanvas2 from './ocg-frontend-analysis/TempCanvas2.vue';
import VtkTwoView from './VtkTwoView.vue';

const props = defineProps({
  columnID: { type: Number, required: true },
  width: { type: Number, default: 0 },
});

const analysisStore = useAnalysisStore();
const viewportStateStore = useViewportStateStore();
const viewID = props.columnID === 1 ? 'AnalysisLeft' : 'AnalysisRight';

const ocgCurveMarkers = ref<Record<OCGCurveType, OCGTerritory[]>>(
  {} as Record<OCGCurveType, OCGTerritory[]>,
);
const curveMarkerTargets = computed<Record<OCGCurveType, OCGTerritory[]>>(
  () => ocgCurveMarkers.value,
);
const isCurveSummaryOpen = ref(false);
const smoothedCurves = ref(false);
const selectedCurveType = ref<string>('');

// ---------------------------------------------------------------------------
// Build config from store (mirrors the old component's onMounted logic)
// ---------------------------------------------------------------------------

const selectedImageIndices = computed(() =>
  viewportStateStore.getSelectedImageIndices(viewID),
);

const biomarkerContainer = computed(() =>
  analysisStore.getBiomarkerContainer(),
);

const hasAnalyzedSelection = computed(() => {
  if (!selectedImageIndices.value) return false;
  const { seriesIdx, sliceIdx } = selectedImageIndices.value;
  return biomarkerContainer.value.isSeriesAnalyzed(seriesIdx, sliceIdx);
});

const selectCurveType = (curveType: OCGCurveType) => {
  const availableT
    = biomarkerContainer.value.getCurveTerritoriesByBiomarkerType(curveType)
      || [];

  selectedCurveType.value = curveType;
  ocgCurveMarkers.value = {
    [curveType]: availableT,
  } as Record<OCGCurveType, OCGTerritory[]>;
};

const availableCurveOptions = computed(() => {
  return (
    biomarkerContainer.value?.getAvailableCurveTypes().map(ct => ({
      value: ct,
      label: biomarkerContainer.value.getLabelFromCurveType(ct),
    })) || []
  );
});

const selectedOptions = computed({
  get: () => {
    const opts = [];
    if (smoothedCurves.value) opts.push('smooth');
    if (isCurveSummaryOpen.value) opts.push('descriptives');
    return opts;
  },
  set: (val: string[]) => {
    smoothedCurves.value = val.includes('smooth');
    isCurveSummaryOpen.value = val.includes('descriptives');
  },
});

const curveOptions = [
  { value: 'smooth', label: 'Smooth Curves' },
  { value: 'descriptives', label: 'Show Statistics' },
];

const initCurveDefaults = () => {
  const container = biomarkerContainer.value;
  if (!container) return;
  const availableCurves = container.getAvailableCurveTypes();
  if (!availableCurves.length) return;

  const legacy = container.getBiomarkerCurveDefaultSelection() as
    | Record<OCGCurveType, OCGTerritory[]>
    | undefined;
  const defaultCurveType
    = ((legacy && Object.keys(legacy)[0]) as OCGCurveType) || availableCurves[0];
  const allAvailable
    = container.getCurveTerritoriesByBiomarkerType(defaultCurveType) || [];

  selectedCurveType.value = defaultCurveType;
  ocgCurveMarkers.value = {
    [defaultCurveType]: allAvailable,
  } as Record<OCGCurveType, OCGTerritory[]>;

  smoothedCurves.value = true;
  isCurveSummaryOpen.value = false;
};

onMounted(() => {
  initCurveDefaults();
});

watch(
  hasAnalyzedSelection,
  (newVal) => {
    if (newVal) {
      initCurveDefaults();
    }
  },
  { immediate: true },
);

watch(availableCurveOptions, (options) => {
  if (
    options.length > 0
    && (!selectedCurveType.value
      || !options.some(o => o.value === selectedCurveType.value))
  ) {
    initCurveDefaults();
  }
});

const startEnd = computed(() => {
  const studyObj = useViewportActions().getStudyObjectByViewerId(viewID);
  if (!studyObj) return { start: 0, end: 0 };
  const breathholdState = studyObj.getBreathhold();
  const start = breathholdState.getStart();
  const end = breathholdState.getEnd();
  return { start, end };
});

const imageIndex = computed(() => {
  const { imageIdx } = selectedImageIndices.value;
  // Clamp imageIdx to start and end
  // The corrected index is imageIdx-start, but clamped to [0, end]
  const { start, end } = startEnd.value;
  const correctedIdx = imageIdx - start;
  return Math.max(0, Math.min(correctedIdx, end));
});

const updateImageIndex = (phaseNumber: number) => {
  const { start, end } = startEnd.value;
  const maxRelativeIndex = Math.max(0, end - start);
  const nextRelativeIndex = Math.max(
    0,
    Math.min(Math.round(phaseNumber), maxRelativeIndex),
  );
  const nextImageIndex = start + nextRelativeIndex;

  if (selectedImageIndices.value.imageIdx === nextImageIndex) {
    return;
  }
  const requestedImageIndexes = {
    seriesIdx: selectedImageIndices.value.seriesIdx,
    sliceIdx: selectedImageIndices.value.sliceIdx,
    imageIdx: nextImageIndex,
  };
  const correctedIndexes = viewportStateStore.getCompatibleImageIndex(
    viewID,
    requestedImageIndexes,
    selectedImageIndices.value,
  );

  viewportStateStore.setSelectedImageIndices(viewID, correctedIndexes);
};
</script>

<template>
  <div class="column-container simple-panel">
    <div class="tempCont">
      <VtkTwoView
        :id="viewID"
        key="sagitalv"
        class="vtk-container"
        :title="`Analysis ${columnID}`"
        :rotate-to-ref-point="true"
      />
    </div>
    <div class="lower-container" @keydown.stop>
      <template v-if="hasAnalyzedSelection">
        <div class="curve-selectors-row mb-2">
          <GlassDropdown
            v-model="selectedCurveType"
            label="Curve:"
            :options="availableCurveOptions"
            @change="selectCurveType"
          />

          <GlassDropdown
            v-model="selectedOptions"
            label="Options"
            :options="curveOptions"
            multiple
          />
        </div>
        <AnalysisGraph
          :viewer-i-d="viewID"
          :target-markers="curveMarkerTargets"
          :width="width"
          :column-i-d="columnID"
          :image-index="imageIndex"
          :smooth-curves="smoothedCurves"
          @update-index="updateImageIndex"
        />
        <div
          v-show="isCurveSummaryOpen"
          class="curve-summary-panel"
          :class="{ open: isCurveSummaryOpen }"
        >
          <h6>Curve Summary Statistics</h6>

          <AnalysisTableCarousel
            :target-markers="curveMarkerTargets"
            :viewer-i-d="viewID"
          />
        </div>
        <div class="my-4 w-[70%] border-t border-gray-300 m-auto" />

        <h5>Sector-Wise Biomarker Maps</h5>

        <div class="gap-2">
          <PolarmapSectorsManager :viewer-i-d="viewID" />
        </div>
        <div class="my-4 w-[70%] border-t border-gray-300 m-auto" />
        <!-- <h5>Global Biomarker Analysis</h5> -->

        <!-- <div class="flex relative gap-2 mb-8">
          <RadarPlot :viewerID="viewID" />
          <ClassificationRadarPlot :viewerID="viewID" />
        </div> -->

        <h6>Biomarker Values</h6>
        <BiomarkerValuesTable :viewer-i-d="viewID" />
      </template>
      <div v-else class="no-analysis-message">
        No Analysis Found for Series Index
        {{ selectedImageIndices.seriesIdx }} Slice Index
        {{ selectedImageIndices.sliceIdx }}.
      </div>
    </div>
  </div>
</template>

<style scoped>
.lower-container {
  overflow-y: scroll;
  -ms-overflow-style: none;
  scrollbar-width: none;
  padding: 5px 15px 0px 15px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  max-height: 100%;
}

.lower-container {
  color: #b1b2bf;
  font-size: 16px;
  font-weight: 500;
}

.curve-summary-header {
  display: flex;
  justify-content: flex-start;
  margin-top: 2px;
}

.curve-summary-toggle {
  display: flex;
  align-items: center;
  border: 0;
  background: transparent;
  color: inherit;
  padding: 0;
  cursor: pointer;
  font: inherit;
  font-weight: 600;
  text-align: left;
}

.curve-summary-toggle:hover {
  color: #e2e8f0;
}

.curve-summary-icon {
  margin-left: 4px;
  transition: transform 0.2s ease;
}

.curve-summary-icon.open {
  transform: rotate(180deg);
}

.curve-summary-panel {
  display: flex;
  flex-direction: column;
  gap: 5px;
  height: 0px;
  overflow: hidden;
  opacity: 0;
}

.curve-summary-panel.open {
  height: fit-content;
  overflow: visible;
  opacity: 1;
}

.no-analysis-message {
  padding: 24px 12px;
  text-align: center;
  line-height: 1.5;
  color: #d1d5db;
}

.lower-container::-webkit-scrollbar {
  display: none;
  width: 0 !important;
}
.column-container {
  display: flex;
  padding: 6px;
  flex-direction: column;
  border-radius: 10px;
  gap: 5px;
}
.tempCont {
  display: flex;
  position: relative;
  justify-content: center;
  min-height: 40%;
  height: fit-content;

  user-select: none;
  overflow: hidden;
}
.header-container {
  display: flex;
  align-items: center;
  justify-content: center;
  padding-left: 10px;
  padding-right: 10px;
}

.curve-selectors-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
  user-select: none;
}
</style>
